import { useEffect, useState } from 'react';
import useFetchAspConnections from '#shared/components/AccountingSoftware/hooks/useFetchAspConnections';
import useFetchAspCustomers from '#shared/components/AccountingSoftware/hooks/useFetchAspCustomers';
import useFetchAspVendors from '#shared/components/AccountingSoftware/hooks/useFetchAspVendors';

/**
 * Resolves whether a contact detail view shows the embedded
 * Receivables/Payables tabs: the accounting feature must be enabled and the
 * record must be linked to a synced accounting-software contact — Receivables
 * needs a match in the customers list, Payables in vendors. `enabled` is the
 * host's accounting feature flag (the portal's
 * AccountingSoftwareIntegrationEnabled locked feature); the drawer only opens
 * from inside the accounting feature, so it passes true.
 */
export default function useAspContactTabs(clientAccountId: string, enabled = true) {
  const { fetchAspConnections } = useFetchAspConnections();
  const { fetchAspCustomers } = useFetchAspCustomers();
  const { fetchAspVendors } = useFetchAspVendors();
  const [resolving, setResolving] = useState(enabled && !!clientAccountId);
  const [receivableContactId, setReceivableContactId] = useState<number>();
  const [payableContactId, setPayableContactId] = useState<number>();

  useEffect(() => {
    // A previous record's ids must never survive into the next one (a host
    // can change the id without remounting).
    setReceivableContactId(undefined);
    setPayableContactId(undefined);

    if (!enabled || !clientAccountId) {
      setResolving(false);

      return undefined;
    }

    setResolving(true);

    // Two lookups can be in flight when the id changes quickly — only the
    // latest one may write.
    let stale = false;

    const resolveContact = async () => {
      try {
        const { Connections } = await fetchAspConnections();
        const [connection] = Connections;
        if (!connection) return;

        const [{ Customers }, { Vendors }] = await Promise.all([
          fetchAspCustomers({ ConnectionID: connection.ConnectionID }),
          fetchAspVendors({ ConnectionID: connection.ConnectionID }),
        ]);

        if (stale) return;

        setReceivableContactId(Customers.find((contact) => contact.ClientAccountID === clientAccountId)?.ContactID);
        setPayableContactId(Vendors.find((contact) => contact.ClientAccountID === clientAccountId)?.ContactID);
      } catch {
        // The lookup only gates the tabs — a failure degrades to no tabs (the
        // API hooks already logged the error).
      } finally {
        if (!stale) setResolving(false);
      }
    };

    resolveContact();

    return () => {
      stale = true;
    };
  }, [clientAccountId, enabled]);

  return { resolving, receivableContactId, payableContactId };
}
